import React, { useEffect, useState } from "react";
import Header from "layout/dictionary/edit/Header";
import { useDispatch, useSelector } from "react-redux";
import { fetchDictionary } from "slices/dictionary/actions/dictionary";
import {
  cleanDictionary,
  editDictionary,
  setSelectedEntry,
  validateCategories,
  validateTerms,
} from "slices/dictionary/dictionarySlice";
import { selectDictionary, selectSettings } from "slices/dictionary/selectors";
import { hideInlineAlert, disableNavBarAndSideBar } from "slices/app/appSlice";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Table, {
  styledTable,
} from "@visiblethread/react-components/dist/components/Table";
import { tableVariants } from "@visiblethread/react-components/dist/components/Table/constants";
import { tableMapper } from "layout/dictionary/edit/tableMapper";
import SubHeader from "layout/dictionary/edit/SubHeader";
import WildcardPopup from "layout/dictionary/edit/WildcardPopup";
import { arraysEqualWithOrder } from "utils";

const selectedEntry = (tableRows, selectedRowIndex) => {
  const getDisplayedRows = (rows) => {
    let parentIndex = -1;
    let isParentCollapsed = false;
    return rows.filter((row) => {
      if (row.parentIndex !== undefined && row.collapsed) {
        parentIndex = row.parentIndex;
        isParentCollapsed = true;
      }
      return !(row.childrenOf === parentIndex && isParentCollapsed);
    });
  };

  let termId = 0;
  const displayedRows = getDisplayedRows(tableRows);
  const selectedRow = displayedRows[selectedRowIndex];
  const id =
    selectedRow?.parentIndex !== undefined
      ? selectedRow?.parentIndex
      : selectedRow?.childrenOf;
  tableRows
    .filter((tr) => tr.childrenOf === selectedRow?.childrenOf)
    .forEach((tr, i) => {
      if (tr === selectedRow) {
        termId = i;
      }
    });
  return {
    type: selectedRow?.parentIndex !== undefined ? "CATEGORY" : "TERM",
    id:
      selectedRow?.parentIndex !== undefined
        ? selectedRow?.parentIndex
        : termId,
    categoryId: selectedRow?.childrenOf,
    termsLength: tableRows.filter((tr) => tr.childrenOf === id).length,
    collapsed: selectedRow?.collapsed,
    addNewTermRow:
      selectedRow?.content[0]?.props?.addChildrenText !== undefined,
  };
};

const DictionaryContent = () => {
  const { id } = useParams();
  const { state: locationState } = useLocation();
  const dictionary = useSelector(selectDictionary);
  const {
    wildcardPopup: { displayed, term },
  } = useSelector(selectSettings);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const categories = dictionary?.categories;
  const { isEditMode } = useSelector(selectSettings);
  const [tableRows, setTableRows] = useState([]);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const [selectedColumnIndex, setSelectedColumnIndex] = useState(-1);
  const { getHeader, getRows, getState } = tableMapper(dispatch, isEditMode);

  useEffect(() => {
    setTableRows(getRows(categories));
  }, [categories, isEditMode]);

  useEffect(() => {
    setSelectedRowIndex(0);
    setSelectedColumnIndex(-1);
  }, [isEditMode]);

  // Map table to data
  useEffect(() => {
    // Only if table rows are different from already existed data
    if (
      tableRows.length > 1 &&
      !arraysEqualWithOrder(tableRows, getRows(categories))
    ) {
      dispatch(
        editDictionary((prevDictionary) => ({
          ...prevDictionary,
          categories: getState(dictionary, tableRows),
        }))
      );

      // Validation
      dispatch(validateCategories());
      dispatch(validateTerms());
    }

    if (selectedRowIndex > tableRows.length - 1) {
      setSelectedRowIndex(tableRows.length - 1);
    }
  }, [tableRows]);

  useEffect(() => {
    const loadDictionary = async () => {
      if (id === undefined) return;

      await dispatch(fetchDictionary(id, navigate));

      if (locationState && locationState.expandCollapseSettings) {
        const { expandCollapseSettings } = locationState;

        dispatch(
          editDictionary((prevDictionary) => {
            const { categories: prevCategories } = prevDictionary;

            if (prevCategories.length !== expandCollapseSettings.length) {
              return prevDictionary;
            }

            return {
              ...prevDictionary,
              categories: prevCategories.map((category, index) => ({
                ...category,
                isExpanded: expandCollapseSettings[index],
              })),
            };
          })
        );
      }
    };

    loadDictionary();
  }, [id]);

  useEffect(() => {
    dispatch(setSelectedEntry(selectedEntry(tableRows, selectedRowIndex)));
  }, [dispatch, selectedRowIndex, tableRows]);

  useEffect(() => {
    return () => {
      dispatch(cleanDictionary());
      dispatch(hideInlineAlert());
      dispatch(disableNavBarAndSideBar(false));
    };
  }, [dispatch]);

  return (
    <div className="edit_dictionary_page" data-testid="dictionary-edit-page">
      {dictionary &&
        Object.prototype.hasOwnProperty.call(dictionary, "name") && (
          <>
            <Header />
            <SubHeader
              selectedRowIndex={selectedRowIndex}
              setSelectedRowIndex={setSelectedRowIndex}
              setSelectedColumnIndex={setSelectedColumnIndex}
            />
            <Table
              id="edit-dictionary-page-table"
              content={{
                header: getHeader(),
                rows: tableRows,
                setRows: setTableRows,
              }}
              config={{
                variant: tableVariants.parentChildren,
                editable: isEditMode,
                draggable: isEditMode,
                table: styledTable.excel,
                resizable: true,
                persistSettings: true,
                selectedRowIndex,
                setSelectedRowIndex,
                selectedColumnIndex,
                setSelectedColumnIndex,
              }}
              dataTestId="dictionary-edit-page-table"
            />
            {displayed && <WildcardPopup term={term} />}
          </>
        )}
    </div>
  );
};

export default DictionaryContent;
